import _ from 'lodash';
import StringUtil from './StringUtil';
import { FuidBidInfoJson } from '../autogenerated/ZoomBridge';
import t from '../i18n/I18n';
import { UPTODATE_NEEDED_ICON, VERSIONED, MODIFIED, NOCHANGE, CONFLICT, POTENTIAL_CONFLICT } from './Constants';

export function getDraggableItemPaths(items) {
  const retItems = [];
  if (items)
    _.each(items, (item) => {
      if (item.extraInfo && StringUtil.isNotEmpty(item.extraInfo.getDragPath())) {
        retItems.push(item.extraInfo.getDragPath());
      } else if (item.wcStatusObject && StringUtil.isNotEmpty(item.wcStatusObject._localPath)) {
        retItems.push(item.wcStatusObject._localPath);
      }
    });
  return retItems;
}

export function isNonAliveAsset(item) {
  return item.isIsDeleted() || item.isArchived() || item.isExpired();
}

export function getNonAliveStatus(item) {
  return item.isExpired() ? 'Expired' : item.getAction();
}

export function getAssetProps(items) {
  let containsFolder = false;
  let containFiles = false;
  let allFromWC = items && items.length > 0;
  let containWCFile = false;
  let containsUpdatePending = false;
  let containsModified = false;
  let containsDeleted = false;
  let containsArchived = false;
  let containsExpired = false;
  let containsAlive = false;
  let containsConflict = false;
  let containsPotentialConflict = false;

  /*
   * lockMenuStatus vals
   * 0 = no status should be shown in context menu
   * 1 = lock status
   * 2 = unlock status
   * */
  let lockMenuStatus = -1;

  _.each(items, (item) => {
    if (item && item.wcStatusObject && StringUtil.isNotEmpty(item.wcStatusObject._localPath)) {
      containWCFile = true;
      const wcStat = item.wcStatusObject._wcStatus;
      if (wcStat === UPTODATE_NEEDED_ICON) {
        containsUpdatePending = true;
      } else if (wcStat === CONFLICT) {
        containsConflict = true;
      } else if (wcStat === MODIFIED) {
        containsModified = true;
      } else if (wcStat === POTENTIAL_CONFLICT) {
        containsPotentialConflict = true;
      }
    } else {
      allFromWC = false;
    }
    if (!containFiles && item && item.getBasicFileAttributes().isFile()) {
      containFiles = true;
    } else if (!containsFolder && item && !item.getBasicFileAttributes().isFile()) {
      containsFolder = true;
    }

    if (lockMenuStatus !== 0) {
      //Authenticator.AUTH_DATA.username
      if (item && item.isIsLocked()) {
        lockMenuStatus = lockMenuStatus === 1 ? 0 : 2;
      } else {
        lockMenuStatus = lockMenuStatus === 2 ? 0 : 1;
      }
    }

    if (item && item.isIsDeleted()) {
      containsDeleted = true;
    } else if (item && item.isArchived()) {
      containsArchived = true;
    }
    if (item && item.isExpired()) {
      containsExpired = true;
    }
    if (item) {
      if (!containsAlive && !item.isIsDeleted() && !item.isArchived() && !item.isExpired()) {
        containsAlive = true;
      }
    }
  });
  return {
    containWCFile: containWCFile,
    containFiles: containFiles,
    containsFolder: containsFolder,
    allFromWC: allFromWC,
    containsModified: containsModified,
    containsUpdatePending: containsUpdatePending,
    containsDeleted: containsDeleted,
    containsArchived: containsArchived,
    containsExpired: containsExpired,
    containsAlive: containsAlive,
    containsConflict: containsConflict,
    lockMenuStatus: lockMenuStatus,
    containsPotentialConflict: containsPotentialConflict
  };
}

export function getLocalPathFuidJsonArray(items): Array<FuidBidInfoJson> {
  let response: Array<FuidBidInfoJson> = [];
  _.each(items, (item) => {
    if (item.wcStatusObject && StringUtil.isNotEmpty(item.wcStatusObject._localPath)) {
      const json: FuidBidInfoJson = new FuidBidInfoJson();
      json.setFuid(item.getFuid());
      json.setLocalPath(item.wcStatusObject._localPath);
      json.setProject(item.getProject());
      json.setBid(item.getBid());
      response.push(json);
    }
  });
  return response;
}

export function getContextMenuStatus(stat, modTime): string {
  let __contextMenuStat;
  let modStat;
  if (stat === VERSIONED || stat === NOCHANGE) {
    __contextMenuStat = t('CHECK_IN');
    modStat = t('LAST_UPDATED', { TIME: modTime });
  } else if (stat === UPTODATE_NEEDED_ICON) {
    __contextMenuStat = t('UPDATE');
    modStat = t('LAST_UPDATED', { TIME: modTime });
  } else if (stat === 'MODIFIED') {
    __contextMenuStat = t('CHECK_IN');
    modStat = t('MODIFIED_ON', { TIME: modTime });
  } else {
    __contextMenuStat = stat;
  }
  return { menuStatus: __contextMenuStat, modStat: modStat };
}

/*
 *  action = -1,0,1,2,3,4,5,6
 *  -1= all
 *  0=Deleted,
 *  1=Archived,
 *  2=Expired,
 *  3=WC-Assets
 *  4=Non-WC-Assets
 *  5=Files
 *  6=Folders
 * */
export function filterAssets(assets, action, fetchRemotePath, fetchAssetId, fetchLocalPath): [] {
  if (!assets) return { filterAssets: assets, remotePaths: undefined, assetIds: undefined, localPaths: undefined };
  const filterAssets = [];
  const remotePaths = fetchRemotePath ? [] : undefined;
  const assetIds = fetchAssetId ? [] : undefined;
  const localPaths = fetchLocalPath ? [] : undefined;

  _.each(assets, (item) => {
    switch (action) {
      case -1:
        addFilterAssetToResponse(filterAssets, item, remotePaths, assetIds, localPaths);
        break;
      case 0:
        if (item.isIsDeleted()) addFilterAssetToResponse(filterAssets, item, remotePaths, assetIds, localPaths);
        break;
      case 1:
        if (item.isArchived()) addFilterAssetToResponse(filterAssets, item, remotePaths, assetIds, localPaths);
        break;
      case 2:
        if (item.isExpired()) addFilterAssetToResponse(filterAssets, item, remotePaths, assetIds, localPaths);
        break;
      case 3:
        if (item.wcStatusObject && StringUtil.isNotEmpty(item.wcStatusObject._localPath))
          addFilterAssetToResponse(filterAssets, item, remotePaths, assetIds, localPaths);
        break;
      case 4:
        if (!item.wcStatusObject || StringUtil.isEmpty(item.wcStatusObject._localPath))
          addFilterAssetToResponse(filterAssets, item, remotePaths, assetIds, localPaths);
        break;
      case 5:
        if (item.getBasicFileAttributes().isFile()) {
          addFilterAssetToResponse(filterAssets, item, remotePaths, assetIds, localPaths);
        }
        break;
      case 6:
        if (!item.getBasicFileAttributes().isFile()) {
          addFilterAssetToResponse(filterAssets, item, remotePaths, assetIds, localPaths);
        }
        break;
    }
  });
  return { filterAssets: filterAssets, remotePaths: remotePaths, assetIds: assetIds, localPaths: localPaths };
}

export function containsAnyFolder(items) {
  for (let item of items) {
    if (!item.getBasicFileAttributes().isFile()) return true;
  }
  return false;
}

export function isAnyNonProxyAsset(items) {
  for (let item of items) {
    if (!item.isProxy()) return true;
  }
  return false;
}

export function isAnyProxyAsset(items) {
  for (let item of items) {
    if (item.isProxy()) return true;
  }
  return false;
}

export function addFilterAssetToResponse(filterAssets, item, remotePaths, assetIds, localPaths) {
  filterAssets.push(item);
  if (remotePaths) remotePaths.push(item.getRemotePath());
  if (assetIds) assetIds.push(item.getFuid());
  if (localPaths && item.wcStatusObject && StringUtil.isNotEmpty(item.wcStatusObject._localPath))
    localPaths.push(item.wcStatusObject._localPath);
}

export function getRatingDisplayLabel(keyValue) {
  var star = String.fromCharCode(9733);
  if (keyValue == 1) return `${star}`;
  else if (keyValue == 2) return `${star}${star}`;
  else if (keyValue == 3) return `${star}${star}${star}`;
  else if (keyValue == 4) return `${star}${star}${star}${star}`;
  else if (keyValue == 5) return `${star}${star}${star}${star}${star}`;
  else return '';
}

export function getRatingSelectedValue(value) {
  if (!value) return value;
  if (value == '*') return '1';
  else if (value == '**') return '2';
  else if (value == '***') return '3';
  else if (value == '****') return '4';
  else if (value == '*****') return '5';
  else return value;
}
